import React, { useState, useEffect, useRef } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import Resizer from "react-image-file-resizer";
import { useNavigate } from "react-router-dom";
import banner from "./assets/img/logo_transparent.png";
import defaultpet from "./assets/img/defaultpet.png";
import { getCurrentCoordinates } from "./Utils";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "https://systalk-dev.azure-api.net/wagtag";

const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY ||
    (process.env.NODE_ENV === "development" ? "1x00000000000000000000AA" : "");

function WizardReportPetFound({ tagId, fromApp }) {
    const navigate = useNavigate();
    const turnstileRef = useRef(null);

    // Wizard step state
    const [step, setStep] = useState(1);
    const [hasWagTag, setHasWagTag] = useState(null);

    const [reportTagId, setReportTagId] = useState("");
    const [description, setDescription] = useState("");
    const [imageBase64, setImageBase64] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [warningMessage, setWarningMessage] = useState("");
    const [petName, setPetName] = useState("");
    const [petImageUri, setPetImagUri] = useState("");
    const [petDescription, setPetDescription] = useState("");
    const [petTemperament, setPetTemperament] = useState(null);
    const [submittingReport, setSubmittingReport] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState("");
    const [turnstileErrorMessage, setTurnstileErrorMessage] = useState("");
    // Track whether the user-entered tag id is valid/active
    const [isTagIdValid, setIsTagIdValid] = useState(null);

    const [petBreed, setPetBreed] = useState("");
    const [petColour, setPetColour] = useState("");
    const [petSize, setPetSize] = useState("");
    const [petSex, setPetSex] = useState("");
    const [alertCode, setAlertCode] = useState(fromApp === "1" ? "Sighting" : "Found");  // only a report sighting button in app so default to sighting
    // Added phone number state
    const [phoneNumber, setPhoneNumber] = useState("");

    useEffect(() => {
        // TagScanPage supplies the normalized scanned tag; an empty value starts the manual report flow.
        if (tagId) {
            setReportTagId(tagId);
            setStep(1); // Always start at welcome
        } else {
            // No tag in the URL -> start on step 2 (ask if the pet has a WagTag)
            setStep(2);
        }

        const fetchPetDetails = async () => {
            if (!tagId) return;

            setReportTagId(tagId);

            try {
                const response = await fetch(`${apiBaseUrl}/api/PetProfile/PetNameImage?tagProductCode=${tagId}`);

                if (!response.ok) {
                    throw new Error("Failed to fetch pet details.");
                }

                const petData = await response.json();

                if (petData) {
                    setPetName(petData.Name);
                    setPetImagUri(petData.ImageUri);
                    setPetDescription(petData?.Description);
                    setPetColour(petData?.Colour);
                    setPetBreed(petData?.Breed);
                    setPetSize(petData?.Size);
                    setPetSex(petData?.Sex);
                    setPetTemperament(petData?.Temperament);

                    setIsTagIdValid(true);
                    setWarningMessage("");
                }
            } catch (error) {
                console.error("Error fetching pet details:", error);
                setWarningMessage("TAG_NOT_REGISTERED");
                setIsTagIdValid(false);
                setStep(1);
            }
        };

        fetchPetDetails();

    }, [tagId]);

    useEffect(() => {
        // Remove hyphens for validation and API call
        const cleanedTagId = reportTagId.replace(/-/g, '');

        // Only fetch if cleanedTagId is 8 digits and was not supplied by the scan route.
        if (
            cleanedTagId &&
            cleanedTagId.length === 8 &&
            cleanedTagId !== tagId
        ) {
            const fetchPetDetails = async () => {
                try {
                    const response = await fetch(`${apiBaseUrl}/api/PetProfile/PetNameImage?tagProductCode=${cleanedTagId}`);
                    if (!response.ok) {
                        throw new Error("Failed to fetch pet details.");
                    }
                    const petData = await response.json();
                    if (petData) {
                        setPetName(petData.Name);
                        setPetImagUri(petData.ImageUri);
                        setPetDescription(petData?.Description);
                        setPetTemperament(petData?.Temperament);
                        setPetColour(petData?.Colour);
                        setPetBreed(petData?.Breed);
                        setPetSize(petData?.Size);
                        setPetSex(petData?.Sex);
                        setReportTagId(cleanedTagId);

                        // Mark as valid + clear warning so Next can enable
                        setIsTagIdValid(true);
                        setWarningMessage("");
                    } else {
                        // Defensive: if API returns no petData, treat as invalid
                        setIsTagIdValid(false);
                        setWarningMessage("This Tag hasn't been activated. Please check the Tag ID or set up the tag in the WagTag app.");
                    }
                } catch (error) {
                    setIsTagIdValid(false);
                    setWarningMessage("Please double check the Tag ID is correct. The Tag ID you've entered doesn't match a pet registered with WagTag.");
                    setPetName("");
                    setPetImagUri("");
                    setPetDescription("");
                    setPetTemperament(null);
                }
            };
            fetchPetDetails();
        }
    }, [reportTagId, tagId]);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            Resizer.imageFileResizer(
                file,
                400, 400,  // Resize dimensions
                "JPEG",
                80,        // Quality percentage
                0,         // Rotation
                (uri) => {
                    setImageBase64(uri);  // Store Base64 image
                },
                "base64"
            );
        }
    };

    // Remove e.preventDefault() since we're not using a form
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Require phone number before reporting
        if (!phoneNumber.trim()) {
            setErrorMessage("Please provide a contact phone number.");
            return;
        }

        if (!reportTagId && !imageBase64) {
            setErrorMessage("Either a Tag ID or a pet image is required to report a found pet.");
            return;
        }

        if (!turnstileToken) {
            setTurnstileErrorMessage("Please complete the security check before submitting the report.");
            return;
        }

        setSubmittingReport(true);
        setErrorMessage("");
        setTurnstileErrorMessage("");

        const { latitude, longitude } = await getCurrentCoordinates();

        // Remove the prefix "data:image/jpeg;base64," before saving the Base64 string
        const base64Image = imageBase64 != null ? imageBase64.replace(/^data:image\/\w+;base64,/, "") : null;

        // Prepend phone number to description
        const combinedDescription = `Call me on ${phoneNumber.trim()}. ${(description || "").trim()}`;

        const apiUrl = `${apiBaseUrl}/api/Alert/ReportPetFound`;
        const payload = {
            ProductCode: reportTagId.replace(/-/g, ''),
            AlertCode: alertCode,
            Latitude: latitude,
            Longitude: longitude,
            Description: combinedDescription,
            ImageBase64: base64Image,
            TurnstileToken: turnstileToken,
        };

        try {
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                const responseBody = await response.json();
                navigate("/thank-you", { state: { latitude: latitude, longitude: longitude, responseBody: responseBody, fromApp: fromApp } });
            } else {
                const errorData = await response.json().catch(() => null);
                const apiErrorMessage = typeof errorData === "string"
                    ? errorData
                    : errorData?.Message || errorData?.message || "Failed to notify the pet owner.";

                setTurnstileToken("");
                turnstileRef.current?.reset();
                setErrorMessage(apiErrorMessage);
            }
        } catch (error) {
            console.error("Error submitting found pet report :", error);
            setErrorMessage("Please double check the Tag ID is correct.  The Tag ID you've entered doesn't match a pet registered with WagTag.");
            setTurnstileToken("");
            turnstileRef.current?.reset();
        } finally {
            setSubmittingReport(false);
        }
    };

    const formatTagId = (tagId) => {
        // Normalize as a string, in case an int is passed in
        const tagIdStr = tagId.toString();

        // Remove any existing hyphens to avoid duplication
        const cleanedTagId = tagIdStr.replace(/-/g, '');

        // If the length is more than 8, truncate to 8 characters
        const truncatedTagId = cleanedTagId.slice(0, 8);

        // If the length is at least 4, add a hyphen after the first 4 characters
        if (truncatedTagId.length > 4) {
            return `${truncatedTagId.slice(0, 4)}-${truncatedTagId.slice(4)}`;
        }

        // If the length is less than or equal to 4, return as is
        return truncatedTagId;
    };

    // Wizard navigation
    const nextStep = () => {
        // Block moving forward out of EnterTagId step if tag invalid
        // In the no-URL flow, EnterTagId is step 3 (when hasWagTag === true)
        if (!tagId && hasWagTag && step === 3) {
            if (isTagIdValid !== true) {
                setWarningMessage(
                    isTagIdValid === false
                        ? "This Tag hasn't been activated. Please check the Tag ID or set up the tag in the WagTag app."
                        : "Please enter a valid 8-digit Tag ID."
                );
                return;
            }
        }
        setStep((s) => s + 1);
    };
    const prevStep = () => setStep((s) => s - 1);

    // Determine if we're on the last step for each flow
    const isLastStep = tagId
        ? step === 3
        : (hasWagTag ? step === 5 : step === 4);

    // Determine if we're on the first step
    const isFirstStep = step === 1;

    const getCurrentStepName = () => {
        if (tagId) {
            switch (step) {
                case 1: return "Welcome";
                case 2: return "ImageUpload";
                case 3: return "ContactDetails";
                default: return "";
            }
        } else {
            switch (step) {
                case 1: return "Welcome";
                case 2: return "AskIfPetHasWagTag";
                case 3: return hasWagTag ? "EnterTagId" : "ImageUpload";
                case 4: return hasWagTag ? "ImageUpload" : "ContactDetails";
                case 5: return hasWagTag ? "ContactDetails" : "";
                default: return "";
            }
        }
    };

    const currentStepName = getCurrentStepName();

    // Minimal: capitalize first characteronly when rendering
    const cap = (s) => (typeof s === "string" && s.length) ? s.charAt(0).toUpperCase() + s.slice(1) : s;

    // Wizard step flow
    const renderStep = () => {
        if (tagId) {
            switch (step) {
                case 1:
                    return renderWelcomeStep();
                case 2:
                    return renderImageUploadStep();
                case 3:
                    return renderContactDetailsStep();
                default:
                    return null;
            }
        } else {
            switch (step) {
                case 1:
                    return renderWelcomeStep();
                case 2:
                    return renderAskIfPetHasWagTagStep();
                case 3:
                    if (hasWagTag) {
                        return renderEnterTagIdStep();
                    } else {
                        return renderImageUploadStep();
                    }
                case 4:
                    if (hasWagTag) {
                        return renderImageUploadStep();
                    } else {
                        return renderContactDetailsStep();
                    }
                case 5:
                    if (hasWagTag) {
                        return renderContactDetailsStep();
                    }
                    return null;
                default:
                    return null;
            }
        }
    };

    function renderWelcomeStep() {
        // If the user entered a tag that isn't registered, show this message
        if (warningMessage === "TAG_NOT_REGISTERED") {
            return (
                <div style={styles.formRow}>
                    <h3>This Tag hasn't been activated</h3>
                    <p style={{ ...styles.infoText, marginTop: "10px" }}>
                        Please install the WagTag app on your phone and follow the instructions to create a new pet profile. This will activate your tag and make it ready for use.
                    </p>
                    <p style={{ ...styles.infoText, marginTop: "10px" }}>
                        For more information see the instructions card that came with your WagTag order, or follow the setup instructions {" "}
                        <a href="https://www.wagtag.com.au/how-to-setup-your-wagtag" target="_blank" rel="noopener noreferrer">here</a>.
                    </p>
                    <p style={{ ...styles.infoText, marginTop: "10px" }}>
                        If you’re trying to report a found pet, please double-check the Tag ID and try again.
                    </p>
                </div>
            );
        }

        return (
            <div style={styles.formRow}>
                <h3>Public Pet Profile</h3>
                <div style={styles.petDetailsGrid}>
                    <div style={styles.detailLabel}>Name:</div>
                    <div style={styles.detailValue}>{cap(petName) || "-"}</div>

                    <div style={styles.detailLabel}>Colour:</div>
                    <div style={styles.detailValue}>{cap(petColour) || "-"}</div>

                    <div style={styles.detailLabel}>Breed:</div>
                    <div style={styles.detailValue}>{cap(petBreed) || "-"}</div>

                    <div style={styles.detailLabel}>Sex:</div>
                    <div style={styles.detailValue}>{cap(petSex) || "-"}</div>

                    <div style={styles.detailLabel}>Size:</div>
                    <div style={styles.detailValue}>{cap(petSize) || "-"}</div>

                    <div style={styles.detailLabel}>Personality:</div>
                    <div style={styles.detailValue}>{cap(petTemperament) || "Timid"}</div>
                </div>
                <div style={{ ...styles.infoText, marginTop: "20px" }}>
                    Press next to report {cap(petName)} found pet or notify the owner of a sighting.
                </div>
            </div>
        );
    }

    function renderAskIfPetHasWagTagStep(){
        return (
            <div style={styles.formRow}>
                <h3>Does the pet have a WagTag?</h3>
                <div style={styles.yesNoButtonRow}>
                    <button
                        style={styles.yesButton}
                        onClick={() => { setHasWagTag(true); nextStep(); }}
                    >
                        Yes
                    </button>
                    <button
                        style={styles.noButton}
                        onClick={() => { setHasWagTag(false); nextStep(); }}
                    >
                        No
                    </button>
                </div>
            </div>
        );
    }

    function renderEnterTagIdStep() {
        const cleaned = reportTagId.replace(/-/g, "");
        const showInlineWarning = isTagIdValid === false;

        return (
            <div style={styles.formRow}>
                <h2>Enter the Tag ID</h2>
                <p>The Tag ID is an 8 digit number located on the back of the WagTag.</p>

                <input
                    id="tagId"
                    type="text"
                    value={formatTagId(reportTagId)}
                    onChange={(e) => {
                        setReportTagId(e.target.value);
                        // optimistic reset while user edits
                        setIsTagIdValid(null);
                        if (warningMessage && warningMessage !== "TAG_NOT_REGISTERED") setWarningMessage("");
                    }}
                    placeholder="Enter the Tag ID"
                    style={styles.textInput}
                />

                {showInlineWarning && (
                    <p style={{ ...styles.errorMessage, marginTop: "10px" }}>
                        This Tag hasn't been activated. Please check the Tag ID or set up the tag in the WagTag app.
                    </p>
                )}

                {cleaned.length === 8 && isTagIdValid === null && (
                    <p style={{ ...styles.infoText, marginTop: "10px" }}>
                        Checking Tag ID...
                    </p>
                )}
            </div>
        );
    }

    function renderImageUploadStep() {
        return (
            <div style={styles.formRow}>
                {!reportTagId ?
                <>
                    <h3>
                        WagTag AI Pet Recognition
                    </h3>
                    <p style={{marginTop: -10}}>
                        WagTag AI will study the picture you provide and attempt to match it to
                        pets in your area.
                    </p>
                </>
                :
                    <p>Add a picture of the pet.  (Optional)</p>
                }
                <div style={styles.imageUploadRow}>
                    <div style={styles.imagePreviewContainer}>
                        <img
                            src={imageBase64 || defaultpet}
                            alt="Pet Preview"
                            style={styles.imagePreview}
                        />
                    </div>
                    <div style={styles.fileInputContainer}>
                        <label htmlFor="imageUpload" style={styles.chooseFileButton}>
                            {imageBase64 ? "Change Picture" : "Add Picture"}
                        </label>
                        <input
                            id="imageUpload"
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            style={styles.hiddenFileInput}
                        />
                    </div>
                </div>
            </div>
        );
    }

    function renderContactDetailsStep() {
        return (
            <div>
                <div style={styles.formRow}>
                    <h3>Contact Details</h3>
                    {/* Added contact phone input */}
                    <input
                        id="contactPhone"
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="Enter your contact phone number"
                        style={styles.textInput}
                    />
                    <textarea
                        id="description"
                        rows="6"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Enter important information such as a nearby location or pick-up address.  This will be shared with the pet owner."
                        style={styles.textArea}
                    ></textarea>
                    <div style={styles.radioGroup}>
                        <label htmlFor="alertFound" style={styles.radioOption}>
                            <input
                                type="radio"
                                id="alertFound"
                                name="alertCode"
                                value="Found"
                                checked={alertCode === "Found"}
                                onChange={(e) => setAlertCode(e.target.value)}
                                style={styles.visuallyHidden}
                            />
                            <span style={styles.customRadioOuter}>
                                {alertCode === "Found" && <span style={styles.customRadioInner} />}
                            </span>
                            <span style={styles.radioLabel}>Pet Found</span>
                        </label>

                        <label htmlFor="alertSighting" style={styles.radioOption}>
                            <input
                                type="radio"
                                id="alertSighting"
                                name="alertCode"
                                value="Sighting"
                                checked={alertCode === "Sighting"}
                                onChange={(e) => setAlertCode(e.target.value)}
                                style={styles.visuallyHidden}
                            />
                            <span style={styles.customRadioOuter}>
                                {alertCode === "Sighting" && <span style={styles.customRadioInner} />}
                            </span>
                            <span style={styles.radioLabel}>Pet Sighting</span>
                        </label>
                    </div>
                    {errorMessage && (
                        <div style={styles.alertError} role="alert">
                            <h3 style={styles.alertErrorTitle}>Report not sent</h3>
                            <p style={styles.alertErrorMessage}>{errorMessage}</p>
                        </div>
                    )}
                    <div style={styles.turnstileContainer}>
                        {turnstileSiteKey ? (
                            <Turnstile
                                ref={turnstileRef}
                                siteKey={turnstileSiteKey}
                                onSuccess={(token) => {
                                    setTurnstileToken(token);
                                    setTurnstileErrorMessage("");
                                }}
                                onExpire={() => setTurnstileToken("")}
                                onError={() => {
                                    setTurnstileToken("");
                                    setTurnstileErrorMessage("The security check could not be completed. Please try again.");
                                }}
                                options={{
                                    action: "report_pet_found",
                                    appearance: "interaction-only",
                                    size: "flexible",
                                    theme: "light",
                                }}
                            />
                        ) : (
                            <p style={styles.errorMessage}>
                                The security check is not configured. Please contact WagTag support.
                            </p>
                        )}
                        {turnstileErrorMessage && (
                            <p style={styles.errorMessage}>{turnstileErrorMessage}</p>
                        )}
                    </div>
                </div>
                <div style={styles.navigationContainer}>
                    <hr style={styles.hr} />
                    <div style={styles.navigationRow}>
                        <div style={{ flex: 1 }}>
                            <button style={styles.navigationButton} onClick={prevStep}>Back</button>
                        </div>
                        <div style={{ flex: 1, textAlign: "right" }}>
                            <button
                                style={{
                                    ...styles.submitButton,
                                    opacity: phoneNumber.trim() && turnstileToken ? 1 : 0.6,
                                    cursor: phoneNumber.trim() && turnstileToken ? "pointer" : "not-allowed",
                                }}
                                onClick={handleSubmit}
                                disabled={!phoneNumber.trim() || !turnstileToken || submittingReport}
                            >
                                Report
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function renderNavigationButtons() {
        // Hide navigation entirely on the "tag not registered" welcome screen
        if (warningMessage === "TAG_NOT_REGISTERED") return null;

        if (isLastStep) return null;

        // Disable Next when on EnterTagId step and invalid/not validated yet
        const isEnterTagStep = (!tagId && hasWagTag && step === 3);
        const nextDisabled = isEnterTagStep && isTagIdValid !== true;

        return (
            <div style={styles.navigationContainer}>
                <hr style={styles.hr} />
                <div style={styles.navigationRow}>
                    <div style={{ flex: 1 }}>
                        {!isFirstStep && (
                            <button style={styles.navigationButton} onClick={prevStep}>
                                Back
                            </button>
                        )}
                    </div>
                    {currentStepName !== "AskIfPetHasWagTag" &&
                        <div style={{ flex: 1, textAlign: "right" }}>
                            <button
                                style={{
                                    ...styles.navigationButton,
                                    opacity: nextDisabled ? 0.6 : 1,
                                    cursor: nextDisabled ? "not-allowed" : "pointer",
                                }}
                                onClick={nextStep}
                                disabled={nextDisabled}
                            >
                                Next
                            </button>
                        </div>
                    }
                </div>
            </div>
        );
    }

    // Only show the pet header if we actually have pet profile data AND we're not in the unregistered flow
    const showPetHeader =
        warningMessage !== "TAG_NOT_REGISTERED" &&
        (Boolean(petName) || Boolean(petImageUri) || Boolean(petDescription));

    return (
        <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#d3d3d3", }}>
            <main style={styles.main}>
                <header style={styles.header}>
                    <div style={styles.headerRow}>
                        <div style={styles.headerImageContainer}>
                            <img
                                src={banner}
                                alt="WagTag"
                                style={styles.headerImage}
                            />
                        </div>

                        <div style={styles.headerTitle}>
                            {alertCode === "Sighting" ? "Report a Sighting" : "Report Pet Found"}
                        </div>
                    </div>
                </header>
                
                {(reportTagId && showPetHeader) &&
                    <>
                        <div style={styles.petHeaderCard}>
                            <div style={styles.petHeaderImageColumn}>
                                <div style={styles.petHeaderImageContainer}>
                                    <img
                                        src={petImageUri || defaultpet}
                                        alt="Pet Preview"
                                        style={styles.petHeaderImage}
                                    />
                                </div>
                            </div>
                            <div style={styles.petHeaderInfo}>
                                <strong>
                                    {petName && `You found ${cap(petName)}. `}
                                </strong>
                                {petDescription && (
                                    <span style={styles.petHeaderDescription}>{petDescription}</span>
                                )}
                            </div>
                            <br/>
                        </div>
                    </>
                }
                <div style={styles.form}>
                    {renderStep()}
                    {renderNavigationButtons()}
                    <footer style={styles.footer}>
                        <p>
                            Visit us at <a href="https://www.wagtag.com.au" target="_blank" rel="noopener noreferrer">www.wagtag.com.au</a>
                        </p>
                    </footer>
                </div>
            </main>
        </div>
    );
}

const styles = {
    header: {
        backgroundColor: "#4CAF50",
        color: "white",
        padding: "6px 10px",
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
    },
    headerRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        width: "100%",
    },
    headerImageContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        overflow: "hidden",
        flexShrink: 0,
    },
    headerImage: {
        width: "auto",
        height: "56px",
        maxHeight: "56px",
        objectFit: "contain",
        borderRadius: "6px",
    },
    headerTitle: {
        flex: 1,
        textAlign: "right",
        fontSize: "18px",
        fontWeight: 700,
        lineHeight: 1.1,
        paddingRight: "2px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    petHeaderCard: {
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        backgroundColor: "#fff",
        padding: "10px",
        gap: "10px",
    },
    petHeaderImageColumn: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "10px",
    },
    petHeaderImageContainer: {
        width: "100px",
        height: "100px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "2px solid #ccc",
        borderRadius: "4px",
        backgroundColor: "#ffffff",
        overflow: "hidden",
        flexShrink: 0,
    },
    petHeaderImage: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: "4px",
    },
    petHeaderInfo: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
    },
    petHeaderDescription: {
        fontSize: "18px",
        color: "#444",
        textAlign: "left",
    },
    petHeaderRow: {
        fontSize: "18px",
        color: "#444",
        marginBottom: "4px",
    },
    reportTitle: {
        fontSize: "24px",
        color: "#333",
    },
    logo: {
        height: "100px",
    },
    main: {
        flex: 1,
        padding: "10px",
        maxWidth: "600px",
        margin: "0 auto",
        width: "95%",
        backgroundColor: "#d3d3d3",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center", // center header + form vertically
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: "5px",
        backgroundColor: "#ffffff",
        paddingLeft: 20,
        paddingRight: 20,
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        minHeight: "50vh",
    },
    formRow: {
        display: "flex",
        flexDirection: "column",
        gap: "5px",
        marginBottom: "5px",
        marginRight: "20px",
        minHeight: "30vh",
    },
    yesNoButtonRow: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center", // vertical centering within available space
        gap: "24px",
        flex: 1,              // consume remaining vertical space in formRow
        // removed: marginTop: "18px"
    },
    navigationContainer: {
        display: "flex",
        flexDirection: "column",
        marginTop: "auto",          // push nav to bottom of the form
    },
    navigationRow: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        gap: "10px",
    },
    imageUploadRow: {
        display: "flex",
        alignItems: "center",
        gap: "20px",
        flexWrap: "nowrap",
    },
    fileInputContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "5px",
        minWidth: "150px",
    },
    uploadLabel: {
        fontSize: "14px",
        color: "#555",
        marginBottom: "5px",
    },
    fileInput: {
        padding: "8px",
        fontSize: "18px",
        cursor: "pointer",
    },
    textInput: {
        width: "100%",
        padding: "10px",
        fontSize: "18px",
        border: "1px solid #ccc",
        borderRadius: "4px",
    },
    textArea: {
        width: "100%",
        padding: "10px",
        fontSize: "18px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        marginTop: "10px",
    },
    imageProfileContainer: {
        width: "120px",
        height: "120px",
        marginLeft: "auto", // Push the image to the right
        border: "2px solid #ccc",
        borderRadius: "8px",
        backgroundColor: "#f9f9f9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
    },
    imagePreviewContainer: {
        width: "100px",
        height: "100px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "2px solid #ccc",
        borderRadius: "4px",
        backgroundColor: "#f9f9f9",
        flexShrink: 0,
    },
    imagePreview: {
        width: "100%",
        maxHeight: "70px", // adjust as needed for banner
        height: "auto",
        objectFit: "contain",
        borderRadius: "4px",
    },
    navigationButton: {
        padding: "16px 32px",        // Increased padding for width and height
        minWidth: "120px",           // Ensures button is wider
        backgroundColor: "#4CAF50",
        color: "white",
        fontSize: "16px",            // Slightly larger font
        fontWeight: "bold",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        alignSelf: "flex-start",
    },
    submitButton: {
        padding: "16px 32px",        // Increased padding for width and height
        minWidth: "120px",           // Ensures button is wider
        backgroundColor: "#4CAF50",
        color: "white",
        fontSize: "16px",            // Slightly larger font
        fontWeight: "bold",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        alignSelf: "flex-start",
    },
    successMessage: {
        color: "green",
        marginTop: "10px",
    },
    errorMessage: {
        color: "red",
        marginTop: "10px",
    },
    alertError: {
        marginTop: "16px",
        padding: "14px 16px",
        backgroundColor: "#fff3f2",
        border: "2px solid #c62828",
        borderRadius: "4px",
    },
    alertErrorTitle: {
        color: "#9f1c1c",
        fontSize: "18px",
        margin: "0 0 6px",
    },
    alertErrorMessage: {
        color: "#5f1717",
        fontSize: "16px",
        lineHeight: 1.4,
        margin: 0,
    },
    footer: {
        backgroundColor: "#ffffff",
        paddingTop: "20px",
        textAlign: "center",
    },
    hiddenFileInput: {
        display: "none", // Hide the file input
    },
    chooseFileButton: {
        padding: "16px 32px",        // Increased padding for width and height
        minWidth: "90px",           // Ensures button is wider
        backgroundColor: "#4CAF50",
        color: "white",
        fontSize: "16px",            // Slightly larger font
        fontWeight: "bold",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        alignSelf: "flex-start",
    },
    infoSection: {
        backgroundColor: "#f9f9f9", // Light gray background
        border: "1px solid #ddd",  // Subtle border
        borderRadius: "8px",       // Rounded corners
        padding: "10px",           // Spacing inside the box
        margin: "5px 0",          // Spacing outside the box
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", // Light shadow for depth
    },
    infoText: {
        fontSize: "18px",          // Slightly larger text
        color: "#333",             // Dark gray text for readability
        lineHeight: "1.",         // Better line spacing
    },
    yesButton: {
        padding: "16px 32px",        // Increased padding for width and height
        minWidth: "90px",           // Ensures button is wider
        backgroundColor: "#4CAF50",
        color: "white",
        fontSize: "16px",            // Slightly larger font
        fontWeight: "bold",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        alignSelf: "flex-start",
    },
    noButton: {
        padding: "16px 32px",        // Increased padding for width and height
        minWidth: "90px",           // Ensures button is wider
        backgroundColor: "#4CAF50",
        color: "white",
        fontSize: "16px",            // Slightly larger font
        fontWeight: "bold",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        alignSelf: "flex-start",
    },
    hr: {
        border: "none",
        borderTop: "1px solid #ccc",
        margin: "20px 0",
        width: "100%",
    },
    petDetailsGrid: {
        display: "grid",
        gridTemplateColumns: "110px 1fr", // fixed label column, flexible value column
        columnGap: "12px",
        rowGap: "6px",
        alignItems: "center",
        marginTop: "6px",
    },
    detailLabel: {
        textAlign: "left",
        fontWeight: 600,
        color: "#555",
    },
    detailValue: {
        textAlign: "left",
        color: "#222",
        wordBreak: "break-word",
    },
    radioGroup: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "32px",
        marginTop: "14px",
        width: "100%",
    },
    turnstileContainer: {
        width: "100%",
        minHeight: "65px",
        marginTop: "16px",
    },
    radioOption: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        cursor: "pointer",
    },
    // Hide input but keep it accessible
    visuallyHidden: {
        position: "absolute",
        opacity: 0,
        width: 0,
        height: 0,
        pointerEvents: "none",
    },
    // Custom radio visuals (bigger and centered)
    customRadioOuter: {
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        border: "2px solid #000",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
    },
    customRadioInner: {
        width: "12px",
        height: "12px",
        borderRadius: "50%",
        backgroundColor: "#4CAF50",
    },
    radioLabel: {
        fontSize: "18px",
        color: "#333",
        cursor: "pointer",
    },
};


export default WizardReportPetFound;
